/**
 * Bracket — shared types and pure helpers used by both the visual bracket
 * and the accessible-table fallback.
 *
 * The goal is to keep "what does this match look like in text" (team names,
 * score strings, venue, winner) in one place, so that the SVG layer, the
 * card markup and the a11y table all derive consistent labels from the
 * same source of truth.
 */

import type { Contestant, Match, SideScore, Venue } from '$utils/getMatchStats';
import {
  getAwayScore,
  getAwayTeam,
  getHomeScore,
  getHomeTeam,
  getVenue,
  getWinner,
} from '$utils/getMatchStats';

/** A single round in the bracket (e.g. "Last 16", "Quarter-finals"). */
export type Round = {
  round: string;
  matches: Match[];
};

/**
 * Resolve a contestant's display label.
 *
 * Falls back to the bracket "phrase" (e.g. "Winner of Match 49") when the
 * team isn't decided yet, then to `fallback` (default "TBD").
 *
 * @param contestant - The home or away contestant from the match feed.
 * @param fallback   - Text to use when neither name nor phrase is set.
 */
export const getTeamLabel = (
  contestant: Contestant | undefined,
  fallback = 'TBD'
): string => {
  // `Contestant` types `phrase` via its index signature as `unknown`, so we
  // narrow to a string before falling through.
  const phrase =
    typeof contestant?.phrase === 'string' ? contestant.phrase : undefined;
  return contestant?.name || fallback;
};

/** Whether a side has a non-negative numeric score recorded. */
export const hasNumericScore = (side: SideScore | undefined): boolean =>
  typeof side?.score === 'number' && side.score >= 0;

/**
 * Format a side's score, with penalty shoot-out in parentheses when present.
 * Returns `"–"` when no score is available.
 *
 * @example
 *   formatScoreText({ score: 2 })           // "2"
 *   formatScoreText({ score: 2, pen: 4 })   // "2 (4 pens)"
 */
export const formatScoreText = (side: SideScore | undefined): string => {
  if (!hasNumericScore(side)) return '–';
  // Non-null assertion is safe — `hasNumericScore` already verified shape.
  return side!.pen ? `${side!.score} (${side!.pen} pens)` : `${side!.score}`;
};

/**
 * Resolve the first venue for a match.
 *
 * `getVenue` returns an array (it can in principle resolve multiple venues
 * for some competition formats); for knockout fixtures we only ever care
 * about the first one. Wrapping the access here means callers don't need
 * to repeat the `[0]` idiom.
 */
export const getMatchVenue = (
  match: Match,
  venues: Venue[]
): Venue | undefined => getVenue(match, venues)[0];

/**
 * Bundle of derived per-match display values used by every bracket view.
 *
 * Computing these once per match (instead of calling each `get*` helper
 * separately in markup) avoids surprises if any of them grow side-effects
 * later and keeps the templating concise.
 */
export type MatchSummary = {
  home: Contestant | undefined;
  away: Contestant | undefined;
  homeLabel: string;
  awayLabel: string;
  homeScore: SideScore | undefined;
  awayScore: SideScore | undefined;
  /** `'home'` / `'away'` / a literal string from the feed / `null`. */
  winner: ReturnType<typeof getWinner>;
  hasScore: boolean;
  venue: Venue | undefined;
};

/**
 * Build a `MatchSummary` for a single match in one pass.
 *
 * @param match    - The raw match from the Opta feed.
 * @param venues   - The hoisted venues array used by `getVenue`.
 * @param fallback - Label to show when a contestant has no name/phrase.
 */
export const getMatchSummary = (
  match: Match,
  venues: Venue[],
  fallback = 'TBD'
): MatchSummary => {
  const home = getHomeTeam(match);
  const away = getAwayTeam(match);
  const homeScore = getHomeScore(match);
  const awayScore = getAwayScore(match);
  return {
    home,
    away,
    homeLabel: getTeamLabel(home, fallback),
    awayLabel: getTeamLabel(away, fallback),
    homeScore,
    awayScore,
    winner: getWinner(match),
    hasScore: hasNumericScore(homeScore) || hasNumericScore(awayScore),
    venue: getMatchVenue(match, venues),
  };
};

/**
 * Resolve the human-readable label for the winning side of a match.
 *
 * @param summary - Pre-computed `MatchSummary`.
 * @param empty   - Fallback when the match is undecided (default "–").
 */
export const getWinnerLabel = (summary: MatchSummary, empty = '–'): string => {
  const { winner, homeLabel, awayLabel } = summary;
  if (winner === 'home') return homeLabel;
  if (winner === 'away') return awayLabel;
  // `getWinner` may return a string (e.g. team name) for some edge cases.
  return winner || empty;
};
